import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { Measure, TimeAllocationType } from '../../api/work-periods-client/work-periods-client.type';
import { useTranslation } from 'react-i18next';
import { PageLayout } from '../../components/page-layout/page-layout';
import { useFeatureFlag } from '../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../helpers/feature-flags/feature-flags.type';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../store/process-analysis-store/process-analysis-store';
import {
  setActivePanel,
  setActiveTab,
  setDateRange,
  setSelectedAllocationType,
  setSelectedBoards,
  setSelectedMeasures,
  setSelectedMonthlyAveragesMeasures,
  setSelectedPortfolios,
  setSelectedTeams,
  setSelectedTaskType,
} from '../../store/process-analysis-store/process-analysis-store.actions';
import {
  useComparisonMode,
  useSelectedMeasureOverTime,
  useSelectedMeasures,
  useSelectedMonthlyAveragesMeasures,
} from '../../store/process-analysis-store/process-analysis-store.hooks';
import { Panel } from '../../store/process-analysis-store/process-analysis-store.type';
import { useFlora } from '../flora/flora.context';
import { MenuDrivenContainer } from '../menu-driven-container/menu-driven-container';
import { AgentsKPIOverview } from './agents-tab/overview/overview';
import { Header } from './header/header';
import { SideSection } from './main-section/measure-comparison/side-section/side-section';
import { KPIOverview } from './overview/overview';
import { ProcessAnalysisFilters } from './process-analysis-filters';
import { ProcessAnalysisMainSection } from './process-analysis-main-section';
import { getProcessAnalysisFloraContext } from './process-analysis.helpers';
import {
  useFiltersReset,
  useProcessAnalysisData,
  useScopedSelectionReset,
  useSideSectionReset,
} from './process-analysis.hooks';
import { ComparisonMode, MultipleViewMode, Tab, ViewMode } from './process-analysis.type';
import { Tabs } from './tabs/tabs';

export const ProcessAnalysis = () => {
  const i18n = useTranslation();
  useDocumentTitle('Process Analysis - Bloomfilter');
  const [searchParams, setSearchParams] = useSearchParams();
  const didInitFromUrlRef = useRef(false);
  const activePanel = useProcessAnalysisStore((state) => state.activePanel);
  const viewMode = useProcessAnalysisStore((state) => state.viewMode);
  const multipleViewMode = useProcessAnalysisStore((state) => state.multipleViewMode);
  const selectedDataPoint = useProcessAnalysisStore((state) => state.selectedDataPoint);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const isAgenticProcessEnabled = useFeatureFlag(FeatureFlag.AgenticProcessByHooks);
  const selectedPortfolios = useProcessAnalysisStore((state) => state.selectedPortfolios);
  const selectedTeams = useProcessAnalysisStore((state) => state.selectedTeams);
  const selectedBoards = useProcessAnalysisStore((state) => state.selectedBoards);
  const startDate = useProcessAnalysisStore((state) => state.startDate);
  const endDate = useProcessAnalysisStore((state) => state.endDate);

  const portfolio = useGlobalStore((state) => state.portfolio);
  const portfolios = useGlobalStore((state) => state.portfolios);
  const teams = useGlobalStore((state) => state.teams);
  const { setContextualInfo } = useFlora();

  const comparisonMode = useComparisonMode();
  const selectedMeasures = useSelectedMeasures();
  const selectedMeasureOverTime = useSelectedMeasureOverTime();
  const selectedMonthlyAveragesMeasures = useSelectedMonthlyAveragesMeasures();

  // Mounts the PA-wide data hooks for their side effects (hydrating the store
  // with initiatives, completion, focus data). Loading state is no longer
  // gated at the page level — each region drives its own skeleton/overlay.
  useProcessAnalysisData();

  useSideSectionReset();
  useFiltersReset();
  // Runs after useFiltersReset so a portfolio switch in Multiple mode clears first
  // and this only has to prune what a Single-mode selection left behind.
  useScopedSelectionReset();

  // URL sync for process analysis filters
  useEffect(() => {
    if (didInitFromUrlRef.current) return;

    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');
    const urlPortfolioIds = searchParams.get('portfolioIds');
    const urlTeamIds = searchParams.get('teamIds');
    const urlBoardIds = searchParams.get('boardIds');
    const urlActiveTab = searchParams.get('activeTab');
    const urlSelectedMeasures = searchParams.get('selectedMeasures');
    const urlTaskType = searchParams.get('taskType')?.trim();
    const urlAllocationType = searchParams.get('allocationType')?.trim();

    let hasChanges = false;

    if (urlActiveTab && Object.values(Tab).includes(urlActiveTab as Tab)) {
      setActiveTab(urlActiveTab as Tab);
      hasChanges = true;
    }

    if (urlStartDate && urlEndDate) {
      setDateRange(urlStartDate, urlEndDate);
      hasChanges = true;
    }

    if (urlPortfolioIds) {
      setSelectedPortfolios(urlPortfolioIds.split(',').filter(Boolean));
      hasChanges = true;
    }

    if (urlTeamIds) {
      setSelectedTeams(urlTeamIds.split(',').filter(Boolean));
      hasChanges = true;
    }

    if (urlBoardIds) {
      setSelectedBoards(urlBoardIds.split(',').filter(Boolean));
      hasChanges = true;
    }

    if (urlSelectedMeasures) {
      const selectedMeasuresFromUrl = urlSelectedMeasures
        .split(',')
        .map((measure) => measure.trim())
        .filter((measure): measure is Measure => Object.values(Measure).includes(measure as Measure))
        .slice(0, 4);

      if (selectedMeasuresFromUrl.length > 0) {
        // Set measures for both single-board and multi-board (monthly averages) views
        setSelectedMeasures(selectedMeasuresFromUrl);
        setSelectedMonthlyAveragesMeasures(selectedMeasuresFromUrl);
        hasChanges = true;
      }
    }

    // Skip the "All work item types" sentinel — it represents no selection,
    // not a real type to send to the backend.
    if (urlTaskType && urlTaskType !== '__all__') {
      setSelectedTaskType(urlTaskType);
      hasChanges = true;
    }

    if (urlAllocationType && Object.values(TimeAllocationType).includes(urlAllocationType as TimeAllocationType)) {
      setSelectedAllocationType(urlAllocationType as TimeAllocationType);
      hasChanges = true;
    }

    didInitFromUrlRef.current = true;

    if (hasChanges) {
      setSearchParams(new URLSearchParams(), { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    // Determine which measures to include based on view mode and comparison mode
    let measures: string[] | undefined;

    if (comparisonMode === ComparisonMode.Single && viewMode === ViewMode.MeasureComparison) {
      // Single mode: measure comparison
      measures = selectedMeasures.map((m) => String(m));
    } else if (comparisonMode === ComparisonMode.Multiple) {
      // Multiple mode: depends on multipleViewMode
      if (multipleViewMode === MultipleViewMode.KeyMeasuresOverTime && selectedMeasureOverTime) {
        measures = [String(selectedMeasureOverTime)];
      } else if (multipleViewMode === MultipleViewMode.MonthlyAverages) {
        measures = selectedMonthlyAveragesMeasures.map((m) => String(m));
      }
    }

    setContextualInfo(
      getProcessAnalysisFloraContext(
        activeTab,
        selectedPortfolios,
        selectedTeams,
        selectedBoards,
        startDate,
        endDate,
        portfolio,
        portfolios,
        teams,
        measures,
      ),
    );
  }, [
    activeTab,
    selectedPortfolios,
    selectedTeams,
    selectedBoards,
    startDate,
    endDate,
    portfolio,
    portfolios,
    teams,
    comparisonMode,
    viewMode,
    multipleViewMode,
    selectedMeasures,
    selectedMeasureOverTime,
    selectedMonthlyAveragesMeasures,
    setContextualInfo,
  ]);

  // Only show side section for measure comparison mode when data point is selected
  const shouldShowSideSection =
    activePanel === Panel.Side && viewMode === ViewMode.MeasureComparison && !!selectedDataPoint;

  return (
    <MenuDrivenContainer header={<Header />}>
      <PageLayout
        controlBar={null}
        overview={{
          title: i18n.t('common.overview.title'),
          content: isAgenticProcessEnabled && activeTab === Tab.Agents ? <AgentsKPIOverview /> : <KPIOverview />,
        }}
        tabs={<Tabs />}
        sideFilters={{
          title: i18n.t('common.filters'),
          content: <ProcessAnalysisFilters />,
          show: activePanel === Panel.Filters,
          setShow: (show: boolean) => setActivePanel(show ? Panel.Filters : null),
        }}
        mainSection={<ProcessAnalysisMainSection />}
        sideSection={<SideSection />}
        showSideSection={shouldShowSideSection}
        data-page-layout
      />
    </MenuDrivenContainer>
  );
};
